import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  useCreateOrUpdateConciseBranch,
  useInvalidateAllConciseBranches,
} from '@festina-life/api'
import { ErrorMessage } from '@festina-life/components'
import {
  assertNotEmpty,
  notEmpty,
  type Optional,
  type SetOptional,
  typeUtils,
} from '@festina-life/core'
import { toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useClose } from '@festina-life/ui/structure'
import { useMemo, useState } from 'react'
import * as Yup from 'yup'

import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { useBranchChildren } from '../useBranchChildren'

type RebaseBranchInput = {
  baseBranch?: Optional<ConciseBranch>
}

type YupRebaseBranchInput = {
  baseBranch?: Optional<SetOptional<ConciseBranch, '_type'>>
}

const validate = typeUtils.createValidator<RebaseBranchInput>()

type Props = {
  branchKey: BranchKey
}

export const ChangeBranchBaseForm = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const [error, setError] = useState<Optional<Error>>()
  const close = useClose()

  const { mutateAsync: updateBranch, isPending } =
    useCreateOrUpdateConciseBranch()
  const invalidateAllConciseBranches = useInvalidateAllConciseBranches()
  const specification = useConciseBranch({
    branchName: branchKey.name,
    server: branchKey.server,
    shouldFallbackToStandard: false,
  })
  const allConciseBranches = useAllConciseBranches()
  const branchChildren = useBranchChildren(branchKey)

  assertNotEmpty(specification)

  const currentBaseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: specification.baseBranchName,
    shouldFallbackToStandard: false,
  })

  const selectableBranches = useMemo(() => {
    const excluded = new Set<string>(branchChildren)
    const serverBranches = allConciseBranches[branchKey.server] ?? []

    if (specification.branchCategory !== 'none') {
      serverBranches.forEach((branch) => {
        if (branch.name && branch.branchCategory === 'none') {
          excluded.add(branch.name)
        }
      })
    }

    excluded.add(branchKey.name)

    return serverBranches.filter(
      (branch) => branch.name && !excluded.has(branch.name),
    )
  }, [
    allConciseBranches,
    branchChildren,
    branchKey.name,
    branchKey.server,
    specification.branchCategory,
  ])

  const isFeatureBranch = specification.branchCategory === 'feature'

  const validationSchema = Yup.object({
    baseBranch: (isFeatureBranch
      ? Yup.object().required(translate('validation.required'))
      : Yup.object()
    )
      .test({
        // Only main branch can be based on null
        name: 'rebaseMainOnNull',
        message: translate('validation.nullRequired'),
        test: (value: Optional<SetOptional<ConciseBranch, '_type'>>) =>
          specification.branchCategory !== 'main' || !value,
      })
      .test({
        // Feature branches must be based on some branch
        name: 'rebaseFeatureOnAny',
        message: translate('validation.required'),
        test: (value: Optional<SetOptional<ConciseBranch, '_type'>>) =>
          specification.branchCategory !== 'feature' || !!value,
      })
      .test({
        // Release branches must be based on main or release branches
        name: 'rebaseReleaseOnMain',
        message: translate('validation.releaseOnMainOrReleaseBranch'),
        test: (value: Optional<SetOptional<ConciseBranch, '_type'>>) =>
          specification.branchCategory !== 'release' ||
          ['main', 'release'].includes(value?.branchCategory ?? ''),
      }),
  }) satisfies Yup.Schema<YupRebaseBranchInput>

  const handleSubmit = async ({ baseBranch }: RebaseBranchInput) => {
    setError(undefined)
    assertNotEmpty(specification.name)
    assertNotEmpty(specification.branchCategory)

    try {
      await updateBranch({
        branchName: specification.name,
        baseBranchName: baseBranch?.name ?? undefined,
        followsBranchName: specification.followsBranchName,
        typeVersions: specification.typesVersionIds?.filter(notEmpty),
        server: branchKey.server,
        category: specification.branchCategory,
      })

      await invalidateAllConciseBranches()

      toast({
        variant: 'success',
        title: translate(
          'branchDetailsPanel.tabs.manage.changeBaseBranch.success',
          {
            branchName: branchKey.name,
            baseBranchName: baseBranch?.name ?? '—',
          },
        ),
      })
      close()
    } catch (error) {
      setError(error)
    }
  }

  return (
    <FormikForm
      initialValues={{ baseBranch: currentBaseBranch ?? undefined }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <FormikSelect
        options={selectableBranches}
        name={validate('baseBranch')}
        label={translate(
          'branchDetailsPanel.tabs.manage.changeBaseBranch.baseBranchLabel',
        )}
        renderFn={(branch) => branch.name ?? ''}
        keyFn={(branch) => branch.name ?? ''}
      />

      {error && <ErrorMessage error={error} />}

      <div className="flex justify-end">
        <OperationsLoadedButton
          variant="primary"
          type="submit"
          isLoading={isPending}
          className="text-right"
        >
          {translate('branchDetailsPanel.tabs.manage.changeBaseBranch')}
        </OperationsLoadedButton>
      </div>
    </FormikForm>
  )
}
